<i18n locale="en">
{
  "loading": "Analyzing image...",
  "noIssues": {
    "title": "No issues found",
    "description": "This image looks great!"
  },
  "reanalyze": "Re-analyze",
  "issueTypes": {
    "lighting": "Lighting",
    "framing": "Framing",
    "focus": "Focus",
    "resolution": "Resolution",
    "composition": "Composition",
    "unappetizing": "Presentation",
    "inaccurate": "Content Accuracy",
    "fake": "AI-Generated"
  }
}
</i18n>

<template>
  <div class="image-analysis-panel">
    <div
      v-if="isLoading"
      class="flex-col gap-3 align-center justify-center py-6"
    >
      <OwnProgressSpinner size="40" />
      <OwnType
        :text="t('loading')"
        variant="paragraph-regular"
        color="secondary"
      />
    </div>

    <template v-else>
      <div
        v-if="!hasIssues"
        class="flex-col gap-3 align-center justify-center py-6"
      >
        <PhCheckCircle class="text-color-success" :size="48" />
        <OwnType
          :text="t('noIssues.title')"
          variant="heading-small"
          color="primary"
          align="center"
        />
        <OwnType
          :text="t('noIssues.description')"
          variant="paragraph-regular"
          color="secondary"
          align="center"
        />
      </div>

      <div v-else class="flex-col gap-3">
        <div
          v-for="finding in findings"
          :key="finding.id"
          class="flex-row gap-3 align-start"
        >
          <div class="icon-container">
            <Component
              :is="getIconForType(finding.name)"
              class="text-color-secondary"
              :size="50"
            />
          </div>
          <div class="flex-col gap-1">
            <OwnType
              :text="`${formatIssueType(finding.name)}:`"
              variant="title-small"
              color="primary"
            />
            <OwnType
              :text="finding.reason"
              variant="paragraph-regular"
              color="secondary"
            />
          </div>
        </div>
      </div>

      <div class="flex-row justify-end mt-3">
        <OwnButton
          variant="tertiary"
          size="small"
          :processing="isReanalyzing"
          @click="$emit('reanalyze')"
        >
          <template #icon-left><PhArrowsClockwise /></template>
          {{ t('reanalyze') }}
        </OwnButton>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import {
  PhArrowsClockwise,
  PhCheckCircle,
  PhWarning,
} from '@phosphor-icons/vue'
import { type Component } from 'vue'
import { useI18n } from 'vue-i18n'

import { OwnButton } from '@/ui/OwnButton'
import { OwnProgressSpinner } from '@/ui/OwnProgressSpinner'
import { OwnType } from '@/ui/OwnType'

import { ISSUE_TYPE_ICONS } from './constants'
import type { MediaFinding } from './types'

interface Props {
  isLoading: boolean
  hasIssues: boolean
  findings: MediaFinding[]
  isReanalyzing?: boolean
}

interface Emits {
  (event: 'reanalyze'): void
}

defineProps<Props>()
defineEmits<Emits>()

const { t } = useI18n()

/**
 * Returns the appropriate icon component for an issue type
 * @param {string} type - The issue type key
 * @returns {Component} The icon component
 */
function getIconForType(type: string): Component {
  return ISSUE_TYPE_ICONS[type] ?? PhWarning
}

/**
 * Formats an issue type key into a human-readable label
 * @param {string} type - The issue type key
 * @returns {string} The formatted label
 */
function formatIssueType(type: string): string {
  const translationKey = `issueTypes.${type}`
  const translated = t(translationKey)

  if (translated !== translationKey) {
    return translated
  }

  return type.charAt(0).toUpperCase() + type.slice(1)
}
</script>

<style scoped>
.image-analysis-panel {
  width: 100%;
}

.icon-container {
  flex-shrink: 0;
  width: 50px;
}
</style>
